import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { StarButton } from "@/components/Watchlist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const PAGE = 25;

const money = (v) => (v == null ? "—" : `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const DirectoryRow = ({ stock }) => {
  const navigate = useNavigate();
  const up = stock.change_percent >= 0;
  return (
    <div className="directory-row" role="button" tabIndex={0} onClick={() => navigate(`/stock/${stock.symbol}`)} onKeyDown={(e) => e.key === "Enter" && navigate(`/stock/${stock.symbol}`)} data-testid={`directory-stock-${stock.symbol}`}>
      <span className="directory-symbol">{stock.symbol}</span>
      <span className="directory-name">{stock.name}<em>{stock.exchange}</em></span>
      <span className="directory-price">{money(stock.price)}</span>
      <span className={`directory-change ${up ? "positive" : "negative"}`}>
        {stock.price == null ? "" : `${up ? "+" : ""}${stock.change_percent.toFixed(2)}%`}
      </span>
      <StarButton symbol={stock.symbol} size={16} />
    </div>
  );
};

export const StockDirectory = () => {
  const [letter, setLetter] = useState("A");
  const [data, setData] = useState({ stocks: [], total: 0, offset: 0, total_listed: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = async (l, offset) => {
    const id = ++seq.current;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/stocks/directory?letter=${l}&offset=${offset}&limit=${PAGE}`);
      if (id === seq.current) setData(res.data);
    } catch (e) {
      if (id === seq.current) setError("Couldn't load stocks. Try again in a moment.");
    } finally {
      if (id === seq.current) setLoading(false);
    }
  };

  useEffect(() => { load(letter, 0); }, [letter]);

  const page = Math.floor(data.offset / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <section className="directory" data-testid="stock-directory">
      <div className="directory-head">
        <h3 className="section-title">Every Stock on the Market</h3>
        <span className="directory-total" data-testid="directory-total">{data.total_listed.toLocaleString()} listed</span>
      </div>
      <div className="letter-bar" data-testid="letter-bar">
        {LETTERS.map((l) => (
          <button key={l} className={`letter-btn ${l === letter ? "active" : ""}`} onClick={() => setLetter(l)} data-testid={`letter-${l}`}>{l}</button>
        ))}
      </div>
      <div className={`directory-list ${loading ? "is-loading" : ""}`} data-testid="directory-list">
        {data.stocks.map((s) => <DirectoryRow key={s.symbol} stock={s} />)}
        {error && <p className="directory-empty" data-testid="directory-error">{error}</p>}
        {!loading && !error && data.stocks.length === 0 && <p className="directory-empty">No stocks under "{letter}"</p>}
      </div>
      <div className="directory-pager">
        <button className="pager-btn" disabled={loading || data.offset === 0} onClick={() => load(letter, data.offset - PAGE)} data-testid="directory-prev">Prev</button>
        <span className="pager-info" data-testid="directory-page">Page {page} of {pages}</span>
        <button className="pager-btn" disabled={loading || !data.has_more} onClick={() => load(letter, data.offset + PAGE)} data-testid="directory-next">Next</button>
      </div>
    </section>
  );
};
